/**
 * Address suggestions while the owner types (plan 7, "Address search").
 * Only the typed text and the area's center are sent to Photon; nothing
 * about the person. Waits until typing pauses, so each word is one request.
 */
import { useCallback, useEffect, useState } from 'react';
import {
  ADDRESS_SEARCH_MIN_CHARS,
  AddressHit,
  AddressLang,
  parsePhoton,
  photonSearchUrl,
} from '../utils/addressSearch';
import type { LatLng } from '../utils/geo';

const TYPING_PAUSE_MS = 400;

export type AddressSearchStatus = 'idle' | 'searching' | 'done' | 'failed';

async function searchPhoton(query: string, lang: AddressLang, near: LatLng, signal?: AbortSignal) {
  const res = await fetch(photonSearchUrl(query, lang, near), { signal });
  if (!res.ok) throw new Error(`Address search ${res.status}`);
  return parsePhoton(await res.json());
}

export function useAddressSearch(query: string, lang: AddressLang, near: LatLng) {
  const [hits, setHits] = useState<AddressHit[]>([]);
  const [status, setStatus] = useState<AddressSearchStatus>('idle');
  const { lat, lng } = near;

  useEffect(() => {
    if (query.trim().length < ADDRESS_SEARCH_MIN_CHARS) {
      setHits([]);
      setStatus('idle');
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      setStatus('searching');
      searchPhoton(query, lang, { lat, lng }, controller.signal)
        .then((found) => {
          setHits(found);
          setStatus('done');
        })
        .catch((err) => {
          if (controller.signal.aborted) return;
          console.warn(err);
          setHits([]);
          setStatus('failed');
        });
    }, TYPING_PAUSE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [query, lang, lat, lng]);

  /** The same place in the other language (same search, matched by OpenStreetMap id). */
  const findInOtherLanguage = useCallback(
    async (picked: AddressHit): Promise<AddressHit | undefined> => {
      try {
        const other = await searchPhoton(query, lang === 'he' ? 'en' : 'he', { lat, lng });
        return other.find((h) => h.key === picked.key);
      } catch (err) {
        console.warn(err);
        return undefined;
      }
    },
    [query, lang, lat, lng]
  );

  return { hits, status, findInOtherLanguage };
}
